<template>
  <div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="金币配置" name="config">
        <ContentWrap title="金币配置">
          <el-form label-width="140px" class="max-w-520px" v-loading="configLoading">
            <el-form-item label="金币名称">
              <el-input
                show-word-limit
                v-model="configForm.coinName"
                maxlength="32"
                placeholder="如：紫金山金币"
              />
            </el-form-item>
            <el-form-item label="每枚对应金额(分)">
              <el-input-number
                :precision="0"
                v-model="configForm.fenPerCoin"
                :min="1"
                :max="100000"
              />
              <span class="ml-12px text-13px text-gray-500"> 例：1 表示 1 金币 = 0.01 元 </span>
            </el-form-item>
            <el-form-item label="开放提现">
              <el-switch v-model="configForm.withdrawEnabled" />
            </el-form-item>
            <el-form-item>
              <el-button
                type="primary"
                :loading="configSaving"
                v-hasPermi="['live:coin:update']"
                @click="handleSaveConfig"
              >
                保存
              </el-button>
            </el-form-item>
          </el-form>
        </ContentWrap>
      </el-tab-pane>

      <el-tab-pane label="会员金币" name="member">
        <ContentWrap title="会员金币">
          <el-form :inline="true" class="-mb-15px">
            <el-form-item label="用户编号">
              <LiveMemberUserSelect v-model="memberQuery.userId" select-class="!w-260px" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleQueryMember">查询</el-button>
            </el-form-item>
          </el-form>
          <el-alert
            v-if="memberInfo && memberInfo.exists === false"
            class="mt-12px max-w-640px"
            type="warning"
            :closable="false"
            title="该用户编号不存在，无法调整金币。请输入真实的会员用户编号后再查询。"
          />
          <el-descriptions
            v-if="memberInfo && memberInfo.exists !== false"
            :column="2"
            border
            class="mt-12px max-w-640px"
          >
            <el-descriptions-item label="用户编号">{{ memberInfo.userId }}</el-descriptions-item>
            <el-descriptions-item label="昵称">{{
              memberInfo.nickname || '—'
            }}</el-descriptions-item>
            <el-descriptions-item label="金币余额">{{ memberInfo.coin }}</el-descriptions-item>
          </el-descriptions>
          <el-form v-if="memberInfo && memberInfo.exists !== false" :inline="true" class="mt-16px">
            <el-form-item label="调整数量">
              <el-input-number
                :min="-2147483647"
                :max="2147483647"
                :precision="0"
                v-model="adjustForm.coinDelta"
              />
              <span class="ml-8px text-12px text-gray-500">正数增加，负数扣减</span>
            </el-form-item>
            <el-form-item label="备注">
              <el-input
                show-word-limit
                :maxlength="235"
                v-model="adjustForm.remark"
                class="!w-220px"
                clearable
              />
            </el-form-item>
            <el-form-item>
              <el-button
                type="warning"
                :loading="adjustSaving"
                v-hasPermi="['live:coin:update']"
                @click="handleAdjust"
              >
                确认调整
              </el-button>
            </el-form-item>
          </el-form>
        </ContentWrap>
      </el-tab-pane>

      <el-tab-pane label="充值记录" name="recharge">
        <ContentWrap>
          <el-form :inline="true" class="-mb-15px">
            <el-form-item label="用户编号">
              <LiveMemberUserSelect v-model="rechargeQuery.userId" select-class="!w-240px" />
            </el-form-item>
            <el-form-item>
              <el-button @click="getRechargeList"
                ><Icon class="mr-5px" icon="ep:search" />搜索</el-button
              >
            </el-form-item>
          </el-form>
          <el-table v-loading="rechargeLoading" :data="rechargeList" stripe>
            <el-table-column label="ID" prop="id" width="80" />
            <el-table-column label="用户" prop="userId" width="100" />
            <el-table-column label="金币" prop="coinAmount" width="90" />
            <el-table-column label="应付(分)" prop="payPrice" width="100" />
            <el-table-column label="状态" width="90">
              <template #default="{ row }">
                <el-tag :type="row.payStatus === 1 ? 'success' : 'warning'">
                  {{ row.payStatus === 1 ? '已支付' : '待支付' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="支付单" prop="payOrderId" min-width="110" />
            <el-table-column
              label="支付时间"
              prop="payTime"
              width="170"
              :formatter="dateFormatter"
            />
            <el-table-column
              label="创建时间"
              prop="createTime"
              width="170"
              :formatter="dateFormatter"
            />
          </el-table>
          <Pagination
            v-model:page="rechargeQuery.pageNo"
            v-model:limit="rechargeQuery.pageSize"
            :total="rechargeTotal"
            @pagination="getRechargeList"
          />
        </ContentWrap>
      </el-tab-pane>

      <el-tab-pane label="提现审核" name="withdraw">
        <ContentWrap>
          <el-form :inline="true" class="-mb-15px">
            <el-form-item label="用户编号">
              <LiveMemberUserSelect v-model="withdrawQuery.userId" select-class="!w-260px" />
            </el-form-item>
            <el-form-item label="状态">
              <el-select
                v-model="withdrawQuery.status"
                clearable
                class="!w-140px"
                placeholder="全部"
              >
                <el-option :value="0" label="待审核" />
                <el-option :value="1" label="待打款" />
                <el-option :value="2" label="已拒绝" />
                <el-option :value="3" label="已打款" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button @click="getWithdrawList"
                ><Icon class="mr-5px" icon="ep:search" />搜索</el-button
              >
            </el-form-item>
          </el-form>
          <el-table v-loading="withdrawLoading" :data="withdrawList" stripe>
            <el-table-column label="ID" prop="id" width="80" />
            <el-table-column label="用户" prop="userId" width="100" />
            <el-table-column label="金币" prop="coinAmount" width="90" />
            <el-table-column label="打款金额" width="120">
              <template #default="{ row }">
                <span class="font-medium">¥{{ formatWithdrawYuan(row.coinAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column
              label="账户信息"
              prop="accountInfo"
              min-width="160"
              show-overflow-tooltip
            />
            <el-table-column label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="withdrawStatusTag(row.status)">{{
                  withdrawStatusLabel(row.status)
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="打款凭证" width="100">
              <template #default="{ row }">
                <el-image
                  v-if="row.payProofUrl"
                  :src="row.payProofUrl"
                  :preview-src-list="[row.payProofUrl]"
                  preview-teleported
                  fit="cover"
                  class="w-48px h-48px"
                />
                <span v-else class="text-gray-400">—</span>
              </template>
            </el-table-column>
            <el-table-column
              label="申请时间"
              prop="createTime"
              width="170"
              :formatter="dateFormatter"
            />
            <el-table-column label="操作" width="220" fixed="right">
              <template #default="{ row }">
                <template v-if="row.status === 0">
                  <el-button
                    link
                    type="success"
                    v-hasPermi="['live:coin:update']"
                    @click="handleAudit(row.id, true)"
                  >
                    通过
                  </el-button>
                  <el-button
                    link
                    type="danger"
                    v-hasPermi="['live:coin:update']"
                    @click="handleAudit(row.id, false)"
                  >
                    拒绝
                  </el-button>
                </template>
                <template v-else-if="row.status === 1">
                  <el-button
                    link
                    type="primary"
                    v-hasPermi="['live:coin:update']"
                    @click="openMarkPaid(row)"
                  >
                    标记打款
                  </el-button>
                </template>
                <span v-else class="text-gray-400">{{
                  row.payRemark || row.auditRemark || '—'
                }}</span>
              </template>
            </el-table-column>
          </el-table>
          <Pagination
            v-model:page="withdrawQuery.pageNo"
            v-model:limit="withdrawQuery.pageSize"
            :total="withdrawTotal"
            @pagination="getWithdrawList"
          />
        </ContentWrap>
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      v-model="paidVisible"
      title="人工打款登记"
      width="480px"
      destroy-on-close
      @closed="resetPaidForm"
    >
      <el-form ref="paidFormRef" :model="paidForm" :rules="paidRules" label-width="100px">
        <el-form-item label="用户">{{ paidForm.userId }}</el-form-item>
        <el-form-item label="金币">{{ paidForm.coinAmount }}</el-form-item>
        <el-form-item label="打款金额"
          >¥{{ formatWithdrawYuan(paidForm.coinAmount) }}</el-form-item
        >
        <el-form-item label="收款账户">{{ paidForm.accountInfo || '—' }}</el-form-item>
        <el-form-item label="打款凭证" prop="payProofUrl">
          <UploadImg v-model="paidForm.payProofUrl" directory="live/coin-withdraw" />
          <div class="text-12px text-gray-400 mt-4px">请先完成线下转账，再上传转账截图/凭证</div>
        </el-form-item>
        <el-form-item label="打款备注" prop="payRemark">
          <el-input
            v-model="paidForm.payRemark"
            type="textarea"
            maxlength="255"
            show-word-limit
            placeholder="可选：流水号、渠道说明等"
          />
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="paidConfirmed">确认已向该用户完成打款</el-checkbox>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="paidVisible = false">取消</el-button>
        <el-button type="primary" :loading="paidSaving" :disabled="!paidConfirmed" @click="submitMarkPaid">
          确认登记
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import * as CoinApi from '@/api/live/coin'
import { UploadImg } from '@/components/UploadFile'
import { dateFormatter } from '@/utils/formatTime'
import LiveMemberUserSelect from '@/views/live/components/LiveMemberUserSelect.vue'

defineOptions({ name: 'LiveCoin' })

const message = useMessage()
const activeTab = ref('config')

const configLoading = ref(false)
const configSaving = ref(false)
const configForm = reactive<CoinApi.LiveCoinConfigVO>({
  coinName: '紫金山金币',
  fenPerCoin: 1,
  withdrawEnabled: false
})

const memberQuery = reactive({ userId: undefined as number | undefined })
const memberInfo = ref<{
  userId: number
  nickname?: string
  coin: number
  exists?: boolean
} | null>(null)
const adjustForm = reactive({ coinDelta: 10, remark: '' })
const adjustSaving = ref(false)

const rechargeLoading = ref(false)
const rechargeList = ref<CoinApi.LiveCoinRechargeVO[]>([])
const rechargeTotal = ref(0)
const rechargeQuery = reactive({
  pageNo: 1,
  pageSize: 10,
  userId: undefined as number | undefined
})

const withdrawLoading = ref(false)
const withdrawList = ref<CoinApi.LiveCoinWithdrawVO[]>([])
const withdrawTotal = ref(0)
const withdrawQuery = reactive({
  pageNo: 1,
  pageSize: 10,
  userId: undefined as number | undefined,
  status: undefined as number | undefined
})

const loadConfig = async () => {
  configLoading.value = true
  try {
    const data = await CoinApi.getConfig()
    Object.assign(configForm, data || {})
  } finally {
    configLoading.value = false
  }
}

const handleSaveConfig = async () => {
  if (!configForm.coinName?.trim() || configForm.coinName.length > 32) {
    message.warning('金币名称不能为空且最多32个字符')
    return
  }
  if (
    !Number.isInteger(configForm.fenPerCoin) ||
    configForm.fenPerCoin < 1 ||
    configForm.fenPerCoin > 100000
  ) {
    message.warning('每枚对应金额须为1至100000的整数')
    return
  }
  configSaving.value = true
  try {
    await CoinApi.saveConfig({ ...configForm })
    message.success('已保存')
  } finally {
    configSaving.value = false
  }
}

const handleQueryMember = async () => {
  const userId = memberQuery.userId
  if (!userId) {
    message.warning('请选择会员用户')
    return
  }
  const data = await CoinApi.getMemberCoin(userId)
  memberInfo.value = data
  if (data && data.exists === false) {
    message.warning('会员用户不存在，请核对用户编号')
  }
}

const handleAdjust = async () => {
  if (!memberInfo.value || memberInfo.value.exists === false) {
    message.warning('会员用户不存在，无法调整')
    return
  }
  if (adjustForm.remark.length > 235) {
    message.warning('备注最多235个字符')
    return
  }
  if (
    !Number.isInteger(adjustForm.coinDelta) ||
    Math.abs(adjustForm.coinDelta) > 2147483647 ||
    !adjustForm.coinDelta
  ) {
    message.warning('调整数量须为非零整数，绝对值不超过2147483647')
    return
  }
  adjustSaving.value = true
  try {
    await CoinApi.adjustMemberCoin({
      userId: memberInfo.value.userId,
      coinDelta: adjustForm.coinDelta,
      remark: adjustForm.remark
    })
    message.success('已调整')
    await handleQueryMember()
  } finally {
    adjustSaving.value = false
  }
}

const getRechargeList = async () => {
  rechargeLoading.value = true
  try {
    const data = await CoinApi.rechargePage({
      pageNo: rechargeQuery.pageNo,
      pageSize: rechargeQuery.pageSize,
      userId: rechargeQuery.userId
    })
    rechargeList.value = data.list || []
    rechargeTotal.value = data.total || 0
  } finally {
    rechargeLoading.value = false
  }
}

/** 按当前金币配置换算打款金额（分→元） */
const formatWithdrawYuan = (coinAmount?: number) => {
  const coins = Number(coinAmount) || 0
  const fen = Number(configForm.fenPerCoin) || 0
  return ((coins * fen) / 100).toFixed(2)
}

const withdrawStatusLabel = (status?: number) => {
  if (status === 0) return '待审核'
  if (status === 1) return '待打款'
  if (status === 2) return '已拒绝'
  if (status === 3) return '已打款'
  return '—'
}
const withdrawStatusTag = (status?: number) => {
  if (status === 0) return 'warning'
  if (status === 1) return 'primary'
  if (status === 2) return 'info'
  if (status === 3) return 'success'
  return 'info'
}

const getWithdrawList = async () => {
  withdrawLoading.value = true
  try {
    const data = await CoinApi.withdrawPage({
      pageNo: withdrawQuery.pageNo,
      pageSize: withdrawQuery.pageSize,
      userId: withdrawQuery.userId,
      status: withdrawQuery.status
    })
    withdrawList.value = data.list || []
    withdrawTotal.value = data.total || 0
  } finally {
    withdrawLoading.value = false
  }
}

const handleAudit = async (id: number, pass: boolean) => {
  await message.confirm(
    pass ? '确认通过该提现？通过后需人工打款并上传凭证。' : '确认拒绝该提现？拒绝将退回金币'
  )
  await CoinApi.auditWithdraw(id, pass, pass ? '审核通过' : '审核拒绝')
  message.success('已处理')
  await getWithdrawList()
}

const paidVisible = ref(false)
const paidSaving = ref(false)
const paidConfirmed = ref(false)
const paidFormRef = ref<FormInstance>()
const paidForm = reactive({
  id: 0,
  userId: 0 as number | undefined,
  coinAmount: 0 as number | undefined,
  accountInfo: '' as string | undefined,
  payProofUrl: '',
  payRemark: ''
})
const paidRules: FormRules = {
  payProofUrl: [{ required: true, message: '请上传打款凭证', trigger: 'change' }]
}

const openMarkPaid = (row: CoinApi.LiveCoinWithdrawVO) => {
  paidForm.id = Number(row.id)
  paidForm.userId = row.userId
  paidForm.coinAmount = row.coinAmount
  paidForm.accountInfo = row.accountInfo
  paidForm.payProofUrl = ''
  paidForm.payRemark = ''
  paidConfirmed.value = false
  paidVisible.value = true
}

const resetPaidForm = () => {
  paidForm.payProofUrl = ''
  paidForm.payRemark = ''
  paidConfirmed.value = false
}

const submitMarkPaid = async () => {
  await paidFormRef.value?.validate()
  if (!paidConfirmed.value) {
    message.warning('请确认已完成打款')
    return
  }
  paidSaving.value = true
  try {
    await CoinApi.markWithdrawPaid({
      id: paidForm.id,
      payProofUrl: paidForm.payProofUrl,
      payRemark: paidForm.payRemark || undefined
    })
    message.success('已登记打款')
    paidVisible.value = false
    await getWithdrawList()
  } finally {
    paidSaving.value = false
  }
}

watch(activeTab, async (tab) => {
  if (tab === 'recharge') getRechargeList()
  if (tab === 'withdraw') {
    await loadConfig()
    await getWithdrawList()
  }
})

onMounted(() => {
  loadConfig()
})
</script>
